import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

// ── Export helpers ────────────────────────────────────────────
const exportCSV = (apps) => {
  const headers = ['App No','NOC Type','Applicant','Mobile','Current Dept','Submitted','Days Pending','Urgency'];
  const rows = apps.map(a => [
    a.application_no, a.noc_name, a.applicant_name, a.applicant_mobile,
    a.current_dept, a.submitted_at ? a.submitted_at.slice(0,10) : '',
    a.days_pending,
    a.days_pending >= 30 ? 'Critical' : a.days_pending >= 14 ? 'High' : 'Overdue'
  ]);
  const csv = [headers, ...rows].map(r => r.map(v => `"${v||''}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url;
  a.download = `pending-report-${new Date().toISOString().slice(0,10)}.csv`;
  a.click(); URL.revokeObjectURL(url);
};

export default function PendingReport() {
  const [apps, setApps]       = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [depts, setDepts]     = useState([]);
  const [filters, setFilters] = useState({ days: 7, dept_id: '' });
  const [triggering, setTrig] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('days', filters.days);
      if (filters.dept_id) params.append('dept_id', filters.dept_id);
      const r = await api.get(`/reports/pending-overdue?${params}`);
      setApps(r.applications || []);
      setSummary(r.summary || []);
    } catch (e) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get('/masters/departments').then(r => setDepts(r.departments || []));
    load();
  }, []);

  const triggerAlert = async () => {
    setTrig(true);
    try {
      await api.post('/admin/check-pending-alerts');
      toast.success('Alert notifications sent to departments!');
    } catch {
      toast.error('Failed to send alerts');
    } finally { setTrig(false); }
  };

  const urgencyColor = (days) => {
    if (days >= 30) return 'bg-red-100 text-red-700 border-red-300';
    if (days >= 14) return 'bg-orange-100 text-orange-700 border-orange-300';
    return 'bg-yellow-100 text-yellow-700 border-yellow-300';
  };

  const urgencyLabel = (days) => {
    if (days >= 30) return '🔴 Critical';
    if (days >= 14) return '🟠 High';
    return '🟡 Overdue';
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">⏰ Pending Applications Report</h1>
          <p className="text-sm text-gray-500 mt-1">
            Applications stuck at departments beyond threshold days
          </p>
        </div>
        <div className="flex gap-2 flex-wrap no-print">
          <button onClick={triggerAlert} disabled={triggering}
            className="btn-warning text-sm">
            {triggering ? '⏳ Sending...' : '🔔 Send Alert Notifications Now'}
          </button>
          <button onClick={() => window.print()}
            className="flex items-center gap-2 text-sm px-4 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 font-medium">
            🖨️ Print
          </button>
          <button onClick={() => exportCSV(apps)} disabled={apps.length === 0}
            className="flex items-center gap-2 text-sm px-4 py-2 rounded-xl bg-green-600 hover:bg-green-700 text-white font-medium disabled:opacity-40">
            📥 Export CSV
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {summary.map((s, i) => (
            <div key={i} className="card border-l-4 border-orange-400">
              <p className="font-semibold text-gray-800 text-sm">{s.dept_name}</p>
              <div className="flex items-center gap-6 mt-2">
                <div>
                  <p className="text-2xl font-bold text-orange-600">{s.count}</p>
                  <p className="text-xs text-gray-400">Pending</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">{s.max_days}</p>
                  <p className="text-xs text-gray-400">Max Days</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="card mb-4 py-3">
        <div className="flex gap-3 flex-wrap items-end">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Pending Since</label>
            <select className="form-select text-sm" value={filters.days}
              onChange={e => setFilters(f => ({ ...f, days: e.target.value }))}>
              <option value="3">3+ days</option>
              <option value="7">7+ days</option>
              <option value="14">14+ days</option>
              <option value="30">30+ days</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Department</label>
            <select className="form-select text-sm" value={filters.dept_id}
              onChange={e => setFilters(f => ({ ...f, dept_id: e.target.value }))}>
              <option value="">All Departments</option>
              {depts.map(d => (
                <option key={d.id} value={d.id}>{d.dept_name}</option>
              ))}
            </select>
          </div>
          <button onClick={load} className="btn-primary text-sm">🔍 Search</button>
        </div>
      </div>

      {/* Count */}
      {!loading && (
        <p className="text-sm text-gray-500 mb-3">
          <span className="font-semibold text-orange-600">{apps.length}</span> applications pending for {filters.days}+ days
        </p>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin w-8 h-8 border-2 border-orange-400 border-t-transparent rounded-full" />
        </div>
      ) : apps.length === 0 ? (
        <div className="card text-center py-16">
          <p className="text-5xl mb-3">✅</p>
          <p className="text-lg font-semibold text-gray-700">No Overdue Applications!</p>
          <p className="text-sm text-gray-400 mt-1">
            All applications are within {filters.days} days
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>App No</th>
                  <th>NOC Type</th>
                  <th>Applicant</th>
                  <th>Mobile</th>
                  <th>Current Dept</th>
                  <th>Submitted</th>
                  <th>Days Pending</th>
                  <th>Urgency</th>
                </tr>
              </thead>
              <tbody>
                {apps.map(app => (
                  <tr key={app.id}>
                    <td className="font-mono text-xs text-blue-700 font-semibold">
                      {app.application_no}
                    </td>
                    <td className="text-sm">{app.noc_name}</td>
                    <td className="text-sm">{app.applicant_name}</td>
                    <td className="text-xs text-gray-500">{app.applicant_mobile}</td>
                    <td className="text-sm font-medium">{app.current_dept}</td>
                    <td className="text-xs text-gray-500">
                      {app.submitted_at
                        ? format(new Date(app.submitted_at), 'dd/MM/yyyy')
                        : '—'}
                    </td>
                    <td>
                      <span className={`text-xs font-bold px-2 py-1 rounded-full border ${urgencyColor(app.days_pending)}`}>
                        {app.days_pending} days
                      </span>
                    </td>
                    <td>
                      <span className="text-xs">{urgencyLabel(app.days_pending)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { font-size: 12px; }
        }
      `}</style>
    </div>
  );
}